export type ActivityChipKind = 'item' | 'request' | 'user' | 'product' | 'supplier' | 'value';

export type ActivitySegment =
  | { kind: 'text'; text: string }
  | { kind: 'chip'; chip: ActivityChipKind; label: string; hint?: string };

export const ACTIVITY_CHIP_ICONS: Readonly<Record<ActivityChipKind, string>> = {
  item: 'receipt_long',
  request: 'shopping_cart',
  user: 'person',
  product: 'inventory_2',
  supplier: 'storefront',
  value: 'payments'
};

/**
 * A mensagem de auditoria é texto livre. Esta leitura por padrão destaca as referências
 * que aparecem nela (pedido, solicitação, usuário, produto, fornecedor, valor) como chips.
 * O que não casar segue como texto. É best-effort: mudança de redação no backend faz o
 * trecho voltar a ser texto, nunca quebra.
 *
 * Ordem das alternativas importa: as formas com aspas e nome vêm antes das formas só com #.
 */
const PATTERN = new RegExp(
  [
    // fornecedor '12345678000199 - NOME' | Fornecedor '#06307074000070 - NOME'
    "(?<supplierKw>fornecedor)\\s+'#?(?<supplierDoc>\\d{11,14})\\s*-\\s*(?<supplierName>[^']+)'",
    // Pedido de solicitação '#2238 - NOME' | pedido '#2238 - NOME'
    "(?<itemKw>pedido(?:\\s+de\\s+solicita[çc][ãa]o)?)\\s+'#?(?<itemCode>\\d+)\\s*-\\s*(?<itemName>[^']+)'",
    // produto '#NOME' (aspa de fechamento pode faltar: para em ' | valor | pelo | foi | fim)
    "(?<productKw>produto)\\s+'#?(?<productName>.+?)(?:'|(?=\\s+valor\\s)|(?=\\s+pelo\\s)|(?=\\s+foi\\s)|(?=\\.\\s|\\.$|$))",
    // valor R$ 5,89
    "(?<valueKw>valor)\\s+(?<value>R\\$\\s?[\\d.]+,\\d{2})",
    // oferta #569 | solicitação de compra #624 | solicitação #624
    "(?<requestKw>oferta|solicita[çc][ãa]o(?:\\s+de\\s+compra)?)\\s+#(?<requestId>\\d+)",
    // pedido #2193
    "(?<itemHashKw>pedido)\\s+#(?<itemHashId>\\d+)",
    // usuário #453
    "(?<userKw>usu[áa]rio)\\s+#(?<userId>\\d+)"
  ].join('|'),
  'giu'
);

export function parseActivityMessage(message: string): ActivitySegment[] {
  const segments: ActivitySegment[] = [];
  if (!message) return segments;

  let cursor = 0;
  for (const match of message.matchAll(PATTERN)) {
    const g = match.groups ?? {};
    const start = match.index ?? 0;
    let keyword: string | undefined;
    let chip: ActivitySegment | undefined;

    if (g['supplierName']) {
      keyword = g['supplierKw'];
      chip = { kind: 'chip', chip: 'supplier', label: g['supplierName'].trim(), hint: `CNPJ ${formatCnpj(g['supplierDoc'])}` };
    } else if (g['itemCode']) {
      keyword = g['itemKw'];
      chip = { kind: 'chip', chip: 'item', label: `#${g['itemCode']} · ${g['itemName'].trim()}` };
    } else if (g['productName']) {
      keyword = g['productKw'];
      chip = { kind: 'chip', chip: 'product', label: g['productName'].trim() };
    } else if (g['value']) {
      keyword = g['valueKw'];
      chip = { kind: 'chip', chip: 'value', label: g['value'].replace(/\s+/g, ' ') };
    } else if (g['requestId']) {
      keyword = g['requestKw'];
      chip = { kind: 'chip', chip: 'request', label: `#${g['requestId']}` };
    } else if (g['itemHashId']) {
      keyword = g['itemHashKw'];
      chip = { kind: 'chip', chip: 'item', label: `#${g['itemHashId']}` };
    } else if (g['userId']) {
      keyword = g['userKw'];
      chip = { kind: 'chip', chip: 'user', label: `#${g['userId']}` };
    }

    if (!chip) continue;
    const before = message.slice(cursor, start) + (keyword ? `${keyword} ` : '');
    if (before) segments.push({ kind: 'text', text: before });
    segments.push(chip);
    cursor = start + match[0].length;
  }

  if (cursor < message.length) segments.push({ kind: 'text', text: message.slice(cursor) });
  return segments;
}

function formatCnpj(digits: string): string {
  if (digits.length !== 14) return digits;
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}
